import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listAllEvents, upsertEvent, deleteEvent, type EventRow } from "@/lib/events.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, Pencil, Plus, Eye, EyeOff, CheckCircle2, AlertCircle, Clock, Calendar, Trophy, Music, Mic, UtensilsCrossed, ArrowRight, ImageIcon, X, Search } from "lucide-react";
import { ImageUpload } from "@/components/admin/ImageUpload";

export const Route = createFileRoute("/_authenticated/admin/events")({
  component: EventsAdmin,
});

type FormState = {
  id?: string;
  title: string;
  starts_at: string;
  ends_at: string;
  tag: string;
  notes: string;
  banner_url: string;
  gallery_urls: string[];
  published: boolean;
};

const empty: FormState = { title: "", starts_at: "", ends_at: "", tag: "", notes: "", banner_url: "", gallery_urls: [], published: true };

function toLocalInput(iso: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}


const ALLOWED_TAGS = ["Live Sport", "Live Music", "Karaoke", "Food Special", "DJ", "Quiz", "Other"] as const;

const FormSchema = z
  .object({
    title: z.string().trim().min(2, "Title must be at least 2 characters.").max(160, "Title is too long (max 160)."),
    tag: z
      .string()
      .trim()
      .min(2, "Tag is required so we can group events on the homepage.")
      .max(40, "Tag is too long (max 40).")
      .refine((v) => ALLOWED_TAGS.includes(v as (typeof ALLOWED_TAGS)[number]), {
        message: `Tag must be one of: ${ALLOWED_TAGS.join(", ")}.`,
      }),
    starts_at: z.string().min(1, "Start date & time is required."),
    ends_at: z.string().optional().or(z.literal("")),
    notes: z.string().max(500, "Notes are too long (max 500).").optional().or(z.literal("")),
    banner_url: z
      .string()
      .max(2000, "Banner URL is too long.")
      .optional()
      .or(z.literal(""))
      .refine(
        (v) => !v || /^https?:\/\//i.test(v) || v.startsWith("/api/public/media/"),
        { message: "Upload an image or paste a URL." },
      ),

    published: z.boolean(),
  })
  .superRefine((v, ctx) => {
    const start = new Date(v.starts_at);
    if (isNaN(start.getTime())) {
      ctx.addIssue({ path: ["starts_at"], code: z.ZodIssueCode.custom, message: "Start date is invalid." });
      return;
    }
    if (v.ends_at) {
      const end = new Date(v.ends_at);
      if (isNaN(end.getTime())) {
        ctx.addIssue({ path: ["ends_at"], code: z.ZodIssueCode.custom, message: "End date is invalid." });
      } else if (end <= start) {
        ctx.addIssue({ path: ["ends_at"], code: z.ZodIssueCode.custom, message: "End must be after start." });
      }
    }
  });

type FieldErrors = Partial<Record<keyof FormState, string>>;

function iconForTag(tag: string | null) {
  const t = (tag ?? "").toLowerCase();
  if (t.includes("sport") || t.includes("match")) return Trophy;
  if (t.includes("music") || t.includes("dj") || t.includes("trad")) return Music;
  if (t.includes("karaoke")) return Mic;
  if (t.includes("food") || t.includes("special")) return UtensilsCrossed;
  return Calendar;
}

function formatEventTime(iso: string) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const tomorrow = new Date(today.getTime() + 86400000).toDateString() === d.toDateString();
  const time = d.toLocaleTimeString("en-IE", { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return `Tonight · ${time}`;
  if (tomorrow) return `Tomorrow · ${time}`;
  return `${d.toLocaleDateString("en-IE", { weekday: "short", day: "2-digit", month: "short" })} · ${time}`;
}

function EventsAdmin() {
  const list = useServerFn(listAllEvents);
  const upsert = useServerFn(upsertEvent);
  const del = useServerFn(deleteEvent);
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [toDelete, setToDelete] = useState<EventRow | null>(null);

  const { data: events = [], isLoading } = useQuery({
    queryKey: ["admin-events"],
    queryFn: () => list(),
  });

  const save = useMutation({
    mutationFn: async () => {
      const parsed = FormSchema.safeParse(form);
      if (!parsed.success) {
        const errs: FieldErrors = {};
        for (const issue of parsed.error.issues) {
          const k = issue.path[0] as keyof FormState;
          if (k && !errs[k]) errs[k] = issue.message;
        }
        setFieldErrors(errs);
        throw new Error("Please fix the highlighted fields.");
      }
      setFieldErrors({});
      return upsert({
        data: {
          id: form.id,
          title: parsed.data.title,
          starts_at: new Date(parsed.data.starts_at).toISOString(),
          ends_at: parsed.data.ends_at ? new Date(parsed.data.ends_at).toISOString() : null,
          tag: parsed.data.tag,
          notes: parsed.data.notes || null,
          banner_url: parsed.data.banner_url || null,
          gallery_urls: form.gallery_urls,
          published: parsed.data.published,
        },
      });
    },
    onSuccess: () => {
      const wasEdit = !!form.id;
      setForm(empty);
      setError(null);
      setFieldErrors({});
      setSuccess(wasEdit ? "Event updated — live on the homepage." : "Event added — live on the homepage.");
      qc.invalidateQueries({ queryKey: ["admin-events"] });
      qc.invalidateQueries({ queryKey: ["public-events"] });
      setTimeout(() => setSuccess(null), 4000);
    },
    onError: (e: Error) => { setError(e.message); setSuccess(null); },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => del({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-events"] });
      qc.invalidateQueries({ queryKey: ["public-events"] });
      setToDelete(null);
    },
  });

  const edit = (e: EventRow) => {
    setFieldErrors({});
    setError(null);
    setSuccess(null);
    setForm({
      id: e.id,
      title: e.title,
      starts_at: toLocalInput(e.starts_at),
      ends_at: toLocalInput(e.ends_at),
      tag: e.tag ?? "",
      notes: e.notes ?? "",
      banner_url: e.banner_url ?? "",
      gallery_urls: e.gallery_urls ?? [],
      published: e.published,
    });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const errCls = "mt-1 flex items-center gap-1 text-xs text-destructive";
  const PreviewIcon = iconForTag(form.tag);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl">Events</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Add what's on tonight — these cards show on the homepage and /events page immediately.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setBusy(true);
            save.mutate(undefined, { onSettled: () => setBusy(false) });
          }}
          className="grid gap-4 rounded-2xl border border-border bg-card/40 p-5 md:grid-cols-2"
        >
          <div className="md:col-span-2">
            <Label>Title *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Liverpool v Arsenal"
              aria-invalid={!!fieldErrors.title}
            />
            {fieldErrors.title && <p className={errCls}><AlertCircle size={12} />{fieldErrors.title}</p>}
          </div>
          <div>
            <Label>Tag *</Label>
            <select
              value={form.tag}
              onChange={(e) => setForm({ ...form, tag: e.target.value })}
              className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              aria-invalid={!!fieldErrors.tag}
            >
              <option value="">Select a tag…</option>
              {ALLOWED_TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            {fieldErrors.tag && <p className={errCls}><AlertCircle size={12} />{fieldErrors.tag}</p>}
          </div>
          <div>
            <Label>Published</Label>
            <div className="mt-2 flex items-center gap-2">
              <input id="pub" type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="h-4 w-4" />
              <label htmlFor="pub" className="text-sm text-muted-foreground">Visible on homepage</label>
            </div>
          </div>
          <div>
            <Label>Starts at *</Label>
            <Input
              type="datetime-local"
              value={form.starts_at}
              onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
              aria-invalid={!!fieldErrors.starts_at}
            />
            {fieldErrors.starts_at && <p className={errCls}><AlertCircle size={12} />{fieldErrors.starts_at}</p>}
          </div>
          <div>
            <Label>Ends at</Label>
            <Input
              type="datetime-local"
              value={form.ends_at}
              onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
              aria-invalid={!!fieldErrors.ends_at}
            />
            {fieldErrors.ends_at && <p className={errCls}><AlertCircle size={12} />{fieldErrors.ends_at}</p>}
          </div>
          <div className="md:col-span-2">
            <ImageUpload
              value={form.banner_url}
              onChange={(v) => setForm({ ...form, banner_url: v })}
              folder="events"
              aspect="video"
              label="Banner image"
              hint="Recommended 1600×900 (16:9). Shown at the top of the event page."
            />
            {fieldErrors.banner_url && <p className={errCls}><AlertCircle size={12} />{fieldErrors.banner_url}</p>}
          </div>
          <div className="md:col-span-2">
            <Label>Gallery ({form.gallery_urls.length})</Label>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Add up to 24 photos. Shown as a tappable gallery with lightbox on the event page.
            </p>
            <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {form.gallery_urls.map((url, idx) => (
                <div key={`${url}-${idx}`} className="relative aspect-square overflow-hidden rounded-lg border border-border bg-muted/30">
                  <img src={url} alt="" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        gallery_urls: form.gallery_urls.filter((_, i) => i !== idx),
                      })
                    }
                    className="absolute right-1 top-1 rounded-full bg-background/85 p-1 text-foreground hover:bg-background"
                    aria-label="Remove photo"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              {form.gallery_urls.length < 24 && (
                <GalleryAddButton
                  onAdd={(url) => setForm({ ...form, gallery_urls: [...form.gallery_urls, url] })}
                />
              )}

            </div>
          </div>

          <div className="md:col-span-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            {fieldErrors.notes && <p className={errCls}><AlertCircle size={12} />{fieldErrors.notes}</p>}
          </div>
          {error && (
            <p className="md:col-span-2 flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive">
              <AlertCircle size={14} /> {error}
            </p>
          )}
          {success && (
            <p className="md:col-span-2 flex items-center gap-2 rounded-md border border-primary/30 bg-primary/10 p-2 text-sm text-primary">
              <CheckCircle2 size={14} /> {success}
            </p>
          )}
          <div className="md:col-span-2 flex gap-2">
            <Button type="submit" disabled={busy}>
              <Plus size={14} className="mr-1" /> {busy ? "Saving…" : form.id ? "Update event" : "Add event"}
            </Button>
            {form.id && (
              <Button type="button" variant="ghost" onClick={() => { setForm(empty); setFieldErrors({}); setError(null); }}>
                Cancel edit
              </Button>
            )}
          </div>
        </form>

        {/* Live preview — mirrors the "Tonight at Plunkets" card */}
        <aside className="lg:sticky lg:top-6 lg:self-start">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Live preview</p>
            <span className="text-[10px] uppercase tracking-wider text-primary/80">Tonight at Plunkets</span>
          </div>
          <div className="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-border bg-card p-6">
            {form.banner_url ? (
              <div className="-mx-6 -mt-6 mb-4 aspect-[16/9] overflow-hidden bg-muted">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={form.banner_url}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                />
              </div>
            ) : (
              <div className="-mx-6 -mt-6 mb-4 flex aspect-[16/9] items-center justify-center bg-muted/40 text-muted-foreground">
                <ImageIcon size={28} />
              </div>
            )}
            <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-primary/10 blur-2xl" />
            <div className="relative">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-primary">
                <PreviewIcon size={14} /> {form.tag || "Tag"}
                {!form.published && <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">Draft</span>}
              </div>
              <h3 className="mt-3 font-display text-2xl font-semibold leading-tight">
                {form.title || "Event title preview"}
              </h3>
              <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
                <Clock size={13} /> {form.starts_at ? formatEventTime(form.starts_at) : "Set a start time"}
              </p>
              {form.notes && <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{form.notes}</p>}
            </div>
            <span className="relative mt-6 inline-flex items-center gap-1 text-sm font-semibold text-primary">
              Reserve <ArrowRight size={14} />
            </span>
          </div>
        </aside>
      </div>

      <div className="rounded-2xl border border-border bg-card/30">
        <div className="border-b border-border px-5 py-3 text-sm font-semibold">All events</div>
        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        ) : events.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">No events yet.</div>
        ) : (
          <ul className="divide-y divide-border">
            {events.map((e) => (
              <li key={e.id} className="flex items-start justify-between gap-4 px-5 py-4">
                <div className="flex min-w-0 gap-3">
                  {e.banner_url ? (
                    <img src={e.banner_url} alt="" className="h-14 w-24 shrink-0 rounded-md object-cover" />
                  ) : (
                    <div className="flex h-14 w-24 shrink-0 items-center justify-center rounded-md bg-muted/40 text-muted-foreground">
                      <ImageIcon size={18} />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-primary">
                      {e.published ? <Eye size={12} /> : <EyeOff size={12} className="text-muted-foreground" />}
                      {e.tag ?? "Event"}
                    </div>
                    <div className="mt-1 font-display text-lg">{e.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(e.starts_at).toLocaleString("en-IE", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      {e.ends_at && ` → ${new Date(e.ends_at).toLocaleTimeString("en-IE", { hour: "2-digit", minute: "2-digit" })}`}
                    </div>
                    {e.notes && <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{e.notes}</div>}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button size="sm" variant="ghost" onClick={() => edit(e)} title="Edit">
                    <Pencil size={14} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setToDelete(e)} title="Delete">
                    <Trash2 size={14} className="text-destructive" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this event?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete && (
                <>
                  <span className="font-semibold text-foreground">"{toDelete.title}"</span> will be removed from the homepage
                  and the events page immediately. This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={remove.isPending}
              onClick={(e) => { e.preventDefault(); if (toDelete) remove.mutate(toDelete.id); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {remove.isPending ? "Deleting…" : "Delete event"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function GalleryAddButton({ onAdd }: { onAdd: (url: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border bg-muted/20 text-muted-foreground transition hover:border-primary hover:text-foreground"
      >
        <Plus size={18} />
        <span className="text-[11px]">Add photo</span>
      </button>
      {open && (
        <div className="col-span-full rounded-lg border border-border bg-card/60 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium">Add gallery photo</span>
            <button type="button" onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
              <X size={14} />
            </button>
          </div>
          <ImageUpload
            value=""
            onChange={(v) => { if (v) { onAdd(v); setOpen(false); } }}
            folder="events/gallery"
            aspect="square"
            label="Photo"
          />
        </div>
      )}
    </>
  );
}

